import { useCallback } from "react";

import { useMutation } from "@apollo/client";

import { ADD_WORKSPACE, WORKSPACES, WorkspaceClientName } from "api/workspace";

import { WorkspaceType } from "models/workspace";

export function useAddWorkspace({
  onSuccess,
  onError
}: {
  // The title comes back so the caller can name the workspace it just created, which it
  // otherwise has no handle on once its own dialog has closed.
  onSuccess?: (workspaceId: string, title: string) => void;
  onError?: (error: string) => void;
} = {}) {
  const [addWorkspaceMutation, { loading: isLoading }] = useMutation(ADD_WORKSPACE);

  const mutate = useCallback(
    (title: string, type: WorkspaceType) => {
      if (!title?.trim()) {
        onError?.("Title is required.");
        return;
      }

      return addWorkspaceMutation({
        variables: {
          workspace: {
            title,
            type,
            dashboards: []
          }
        },
        context: {
          clientName: WorkspaceClientName
        },
        refetchQueries: [
          { query: WORKSPACES, context: { clientName: WorkspaceClientName } }
        ],
        onCompleted: (data) => {
          const newId = data?.addWorkspace?.workspaceId;
          if (!newId) {
            // A response with no id is a failed create, not a silent no-op.
            onError?.("Workspace could not be created.");
            return;
          }

          // Selecting the new workspace is the caller's call, not this hook's: whether the
          // jump is welcome depends on whether the user is still in the create flow when
          // the response lands.
          onSuccess?.(newId, title);
        },
        onError: (error) => {
          onError?.(error.message);
        }
      });
    },
    [addWorkspaceMutation, onSuccess, onError]
  );

  return { mutate, isLoading };
}
